import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, MousePointerClick } from "lucide-react";
import { MarketingShell, Container, Reveal, Eyebrow, TrialButton, IMG, useSeo, FONT, JsonLd, SITE } from "./Marketing";
import { track } from "../../lib/analytics";

const PAGES = {
  "gym-turf-designer": {
    title: "Gym Turf Designer Software",
    metaTitle: "Gym Turf Designer Software — Let Customers Design Their Turf Online",
    metaDesc: "The #1 gym turf designer software. Let customers design custom gym sprint tracks online, get an instant price and request a quote. 14-day free trial.",
    eyebrow: "For gym turf specialists",
    h1: "The Gym Turf Designer Your Customers Actually Want To Use",
    sub: "Give gym owners and PTs a tool to design their own sprint tracks, sled lanes and branded turf — then capture them as qualified quote leads.",
    img: IMG.gymFloor,
    points: ["Custom sizes, colours, markings & logos", "AI concepts from a gym's branding in seconds", "Instant to-scale pricing", "Every design becomes a lead"],
  },
  "artificial-turf-designer": {
    title: "Artificial Turf Designer",
    metaTitle: "Artificial Turf Designer — Online Turf Design & Quote Tool",
    metaDesc: "Add an artificial turf designer to your website. Let customers visualise and design custom turf, then request a quote instantly. Start a 14-day free trial.",
    eyebrow: "For artificial turf installers",
    h1: "An Artificial Turf Designer That Wins You The Quote",
    sub: "Let customers design and visualise their artificial turf project before they buy — and hand you a fully specced enquiry, not a vague email.",
    img: IMG.turf,
    points: ["Interactive, to-scale turf preview", "Colours, borders & markings", "Instant price estimates", "Qualified leads to your inbox"],
  },
  "gym-flooring-software": {
    title: "Gym Flooring Software",
    metaTitle: "Gym Flooring Software — Interactive Designer & Lead Capture",
    metaDesc: "Gym flooring software that turns your website into a sales tool. Customers design their flooring, see a price and enquire. 14-day free trial, no card needed.",
    eyebrow: "For gym flooring companies",
    h1: "Gym Flooring Software That Turns Browsers Into Buyers",
    sub: "Stop losing website visitors to a boring contact form. Give them an interactive flooring designer that captures high-intent leads automatically.",
    img: IMG.fitness,
    points: ["Embed on any website in minutes", "Fully branded to your company", "AI-generated flooring concepts", "Conversion analytics built in"],
  },
  "turf-visualiser": {
    title: "Turf Visualiser",
    metaTitle: "Turf Visualiser — Let Customers See Their Turf Before They Buy",
    metaDesc: "A turf visualiser for your website. Customers design and preview their custom turf to scale, get a price and request a quote. Try the live demo free.",
    eyebrow: "For sports & fitness flooring",
    h1: "Let Customers See Their Turf Before They Buy",
    sub: "A visual, interactive turf visualiser that removes guesswork, reduces revisions and turns interest into enquiries.",
    img: IMG.arena,
    points: ["Real-time visual representation", "Multi-currency instant pricing", "Two-lane & sprint track layouts", "Leads with full design specs"],
  },
};

export default function Seo({ slug }) {
  const p = PAGES[slug] || PAGES["gym-turf-designer"];
  useSeo(p.metaTitle, p.metaDesc);
  useEffect(() => { track("homepage_view", { page: slug }); }, [slug]);

  return (
    <MarketingShell>
      <JsonLd id={`ld-seo-${slug}`} data={{
        "@context": "https://schema.org",
        "@graph": [
          { "@type": "Service", "name": p.title, "serviceType": p.title, "provider": { "@id": SITE.url + "/#org" }, "areaServed": "GB", "url": SITE.url + "/" + slug, "description": p.metaDesc },
          { "@type": "BreadcrumbList", "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE.url + "/" },
            { "@type": "ListItem", "position": 2, "name": p.title, "item": SITE.url + "/" + slug },
          ] },
        ],
      }} />
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0"><img src={p.img} alt="" className="w-full h-full object-cover opacity-30" /><div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/75 via-[#0a0a0a]/88 to-[#0a0a0a]" /></div>
        <Container className="relative py-24 md:py-32 max-w-4xl">
          <Reveal><Eyebrow>{p.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.05}><h1 className="mt-5 text-4xl md:text-6xl font-black uppercase tracking-tighter text-white leading-[0.95]" style={{ fontFamily: FONT.display }}>{p.h1}</h1></Reveal>
          <Reveal delay={0.1}><p className="mt-6 text-lg text-zinc-300 max-w-2xl leading-relaxed">{p.sub}</p></Reveal>
          <Reveal delay={0.16}>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <TrialButton source={`seo-${slug}`} testid="seo-start-trial" />
              <a href="/demo" onClick={() => track("demo_cta_clicked", { source: `seo-${slug}` })} data-testid="seo-try-demo"
                className="inline-flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-md border border-white/20 text-white hover:bg-white/5 transition-colors">
                Try The Demo <MousePointerClick size={16} />
              </a>
            </div>
          </Reveal>
        </Container>
      </section>

      <section className="py-20 md:py-28">
        <Container className="grid md:grid-cols-2 gap-4 max-w-4xl">
          {p.points.map((pt, i) => (
            <Reveal key={pt} delay={i * 0.06}>
              <div className="flex items-start gap-3 bg-[#161616] border border-white/5 rounded-xl p-5">
                <Check className="text-[#00D26A] shrink-0 mt-0.5" size={20} /><span className="text-zinc-200">{pt}</span>
              </div>
            </Reveal>
          ))}
        </Container>
        <Container className="text-center mt-12">
          <TrialButton source={`seo-${slug}-mid`}>Start Your 14-Day Free Trial <ArrowRight size={16} className="ml-2" /></TrialButton>
        </Container>
      </section>
    </MarketingShell>
  );
}
